import { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import MetricOptionsContext from '../../contexts/MetricOptionsContext';
import { isKpiRow } from '../../hooks/kpiTypeCheckers';
import isDefined from '../../isDefined';
import metricTypeCheckers from '../../types/metricTypeCheckers';

const useScorecardDatasets = (scorecard: Scorecards.Scorecard) => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const getDatasets = useCallback(() => {
    const kpiMetrics = scorecard.kpis
      .filter(isKpiRow)
      .map((kpi) => metricOptionsLookup[kpi.metricId])
      .filter(isDefined);

    const normalMetrics = kpiMetrics
      .filter(metricTypeCheckers.isNotSpecialMetric)
      .reduce((normalMetrics: Metrics.NormalMetric[], currentMetric) => {
        if (metricTypeCheckers.isNormalMetric(currentMetric)) {
          return [...normalMetrics, currentMetric];
        }

        const subMetrics = currentMetric.metricIds
          .map((mid) => metricOptionsLookup[mid])
          .filter(isDefined)
          .filter(metricTypeCheckers.isNormalMetric);

        return [...normalMetrics, ...subMetrics];
      }, []);

    // Special metrics query a real dataset too (e.g. driver status → driverStatus),
    // and scorecard filters are applied to that query — so include their datasets.
    const specialMetricDatasets = kpiMetrics
      .filter(metricTypeCheckers.isSpecialMetric)
      .map((m) => m.dataType);

    return _.uniq([
      ...normalMetrics.map((m) => m.dataType),
      ...specialMetricDatasets,
    ]);
  }, [metricOptionsLookup, scorecard.kpis]);
  const [datasets, setDatasets] = useState<string[]>(() => getDatasets());
  useEffect(() => {
    setDatasets(getDatasets());
  }, [getDatasets]);

  return datasets;
};

export default useScorecardDatasets;
